/**
 * useNow — the app's one shared wall clock, as a useSyncExternalStore hook.
 *
 * `useNow(resolutionMs)` returns the current epoch timestamp rounded DOWN to
 * `resolutionMs`, re-rendering the subscriber only when that rounded value
 * actually changes. All subscribers share ONE module-level interval, started
 * when the first component subscribes and cleared when the last unsubscribes;
 * with mixed resolutions the interval runs at the minimum active resolution
 * (a coarser subscriber is notified on every tick but its rounded snapshot —
 * and therefore its render — only changes at its own cadence).
 *
 * `useNow(null)` means "no time-dependent UI right now": the component is not
 * subscribed, contributes no timer, and never re-renders on time. The value
 * it returns is the store's PARKED last-known now — arbitrarily stale if
 * nothing app-wide is subscribed — so never age UI against it; take a wall
 * reading instead (see selectBandNow in utils/batchRollup.ts).
 *
 * This replaces per-component setInterval + setState ticks (#113 review):
 * zero timers exist app-wide unless something is actually watching the clock.
 */

import { useCallback, useSyncExternalStore } from 'react'

interface Subscriber {
  resolutionMs: number
  notify: () => void
}

const subscribers = new Set<Subscriber>()
let currentNow = Date.now()
let timer: ReturnType<typeof setInterval> | null = null
let timerResolutionMs = 0

/** (Re)start or stop the single interval to match the minimum active resolution. */
function retune(): void {
  let min = Infinity
  for (const s of subscribers) min = Math.min(min, s.resolutionMs)
  if (min === Infinity) {
    if (timer !== null) { clearInterval(timer); timer = null }
    return
  }
  if (timer !== null) {
    if (timerResolutionMs === min) return
    clearInterval(timer)
  }
  timerResolutionMs = min
  timer = setInterval(() => {
    currentNow = Date.now()
    for (const s of subscribers) s.notify()
  }, min)
}

/** Store subscribe half — exported for unit tests; components use useNow. */
export function subscribeNow(resolutionMs: number, notify: () => void): () => void {
  // Refresh the shared clock on a new subscription (and tell everyone), so a
  // component mounting long after the last tick doesn't render a stale time.
  currentNow = Date.now()
  const sub: Subscriber = { resolutionMs, notify }
  subscribers.add(sub)
  retune()
  for (const s of subscribers) {
    if (s !== sub) s.notify()
  }
  return () => {
    subscribers.delete(sub)
    retune()
  }
}

/** Store snapshot half — exported for unit tests; components use useNow. */
export function nowSnapshot(resolutionMs: number | null): number {
  if (resolutionMs === null) return currentNow
  return Math.floor(currentNow / resolutionMs) * resolutionMs
}

export function useNow(resolutionMs: number | null): number {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      resolutionMs === null ? () => {} : subscribeNow(resolutionMs, onStoreChange),
    [resolutionMs],
  )
  const getSnapshot = useCallback(() => nowSnapshot(resolutionMs), [resolutionMs])
  return useSyncExternalStore(subscribe, getSnapshot)
}
